import {
    StripeProvider
} from './stripe.type';

export type OneTimePaymentKey = 'ss_mat_naltrex_consult'

export enum PlanKeyEnum {
    SS_DIGITAL = 'ss_digital',
    SS_MAT_NALTREX = 'ss_mat_naltrex'
}

export type PlanKey = PlanKeyEnum.SS_DIGITAL | PlanKeyEnum.SS_MAT_NALTREX;

export type PlanTier = 'base' | 'premium';

/**
 * Without a variant the prices API returns no premium plans (every one has a variant).
 * '2' (prem_298) is v1's default.
 */
export const DEFAULT_PREMIUM_VARIANT = '2';

export type SubscriptionPlanFrequencyPeriod = 'day' | 'week' | 'month' | 'year';

/** 'annual' here is 'yearly' on the composed entity's `subscriptionType` — two vocabularies. */
export type UserSubscriptionFrequency = 'monthly' | 'quarterly' | 'annual';

export type PaymentProcessorName = 'stripe' | 'google-play' | 'apple-app-store';

export type PaymentProcessor = {
    id: string,
    createdAt: string,
    updatedAt: string,
    name: PaymentProcessorName,
    /**
     * A lie when `name !== 'stripe'`: the column also holds Play and Appstore keys, and
     * tsc cannot catch the misuse. Read it via resolveStripeProvider().
     */
    key: StripeProvider
}

export type PaymentProcessorCustomer = {
    id: string,
    createdAt: string,
    updatedAt: string,
    externalCustomerId: string,
    /**
     * "Has a card on file", Stripe-only: store rows always carry a payment method
     * unless canceled, and the Play purchase handler never increments it.
     */
    paymentMethodCount: number,
    paymentProcessor: PaymentProcessor,
    paymentProcessorId: string
}

export type SubscriptionPlan = {
    id: string,
    createdAt: string,
    updatedAt: string,
    tier: PlanTier,
    externalPlanId: string,
    key: PlanKey,
    variant?: string,
    processor?: PaymentProcessor,
    processorId?: string
}

export type SubscriptionPlanFrequency = {
    id: string,
    createdAt: string,
    updatedAt: string,
    period: SubscriptionPlanFrequencyPeriod,
    interval: number
}

export type SubscriptionPricing = {
    id: string,
    createdAt: string,
    updatedAt: string,
    price: number,
    currency: string,
    externalPriceId?: string,
    plan: SubscriptionPlan,
    planId: string,
    frequency: SubscriptionPlanFrequency,
    frequencyId: string
}

export enum UserSubscriptionStatusEnum {
    INCOMPLETE = 'incomplete',
    INCOMPLETE_EXPIRED = 'incomplete_expired',
    TRIALING = 'trialing',
    ACTIVE = 'active',
    PAST_DUE = 'past_due',
    CANCELED = 'canceled',
    GRACE_PERIOD = 'grace_period'
}

export type UserSubscriptionStatus = UserSubscriptionStatusEnum.INCOMPLETE
    | UserSubscriptionStatusEnum.INCOMPLETE_EXPIRED
    | UserSubscriptionStatusEnum.TRIALING
    | UserSubscriptionStatusEnum.ACTIVE
    | UserSubscriptionStatusEnum.PAST_DUE
    | UserSubscriptionStatusEnum.CANCELED
    | UserSubscriptionStatusEnum.GRACE_PERIOD

export type UserSubscription = {
    id: string,
    creeatedAt: string,
    updatedAt: string,
    pricing: SubscriptionPricing,
    pricingId: string,
    plan: SubscriptionPlan,
    planId: string,
    externalSubscriptionId: string,
    status: UserSubscriptionStatus,
    periodStart: string,
    periodEnd: string,
    cancelAtPeriodEnd: boolean,
    eventType: string,
    eventTime: string,
    eventExternalId: string,
    paymentProcessorCustomer: PaymentProcessorCustomer,
    paymentProcessorCustomerId: string,
    /**
     * Appended per row by the controller, not a column; the lookup is keyed on the
     * API's own NODE_ENV and misses yield undefined — prefer `pricing.frequency`.
     */
    subscriptionFrequency?: UserSubscriptionFrequency
}

/** `pending`: nothing paid yet. `completed`: term reached. The rest only occur after a cancel. */
export type SubscriptionCommitmentStatus = 'pending'
    | 'active'
    | 'enforcing'
    | 'enforced'
    | 'completed'
    | 'released';

/** Mirrors rest-api's `CommitmentCancelQuote` (`GET stripe/commitment/:planKey`). Amounts are minor units. */
export type CommitmentCancelQuote = {
    status: SubscriptionCommitmentStatus,
    commitmentPeriods: number,
    periodsPaid: number,
    intervalPeriod: SubscriptionPlanFrequencyPeriod,
    intervalCount: number,
    commitmentEndAt: string,
    currentPeriodEnd: string,
    /** Past due is cancelled outright rather than at period end, and never charged a fee. */
    cancelsImmediately: boolean,
    /** Zero whenever a cancel now would not be charged. */
    fee: {
        periodsRemaining: number,
        amount: number,
        currency: string
    },
    /** When cancelling starts to cost more this period — typically the fee-free window closing. */
    upcomingFee: {
        startsAt: string,
        periodsRemaining: number,
        amount: number
    } | null
}

/** `fix` is what clears it: healing, adding a commitment, or (`manual`) a person. */
export type AdminCommitmentIssue = {
    code: string,
    fix: 'heal' | 'add' | 'manual',
    detail: string
}

/**
 * Mirrors rest-api's `CommitmentOverviewItem` (`GET stripe/admin/commitment/:userId`), as far as the
 * admin panel reads it. Amounts are minor units.
 */
export type AdminCommitmentOverviewItem = {
    externalSubscriptionId: string,
    externalPriceId: string,
    subscription: {
        stripeStatus: string,
        currentPeriodEnd: string,
        cancelling: boolean,
        paused: boolean
    },
    commitment: {
        id: string,
        status: SubscriptionCommitmentStatus,
        commitmentPeriods: number,
        periodsPaid: number,
        commitmentEndAt: string,
        releaseReason: string | null,
        waivedAt: string | null,
        waivedBy: string | null
    } | null,
    fees: {
        id: string,
        createdAt: string,
        outcome: string,
        amount: number,
        currency: string,
        failureMessage: string | null
    }[],
    quote: CommitmentCancelQuote | null,
    issues: AdminCommitmentIssue[]
}

export type AdminCommitmentOverview = {
    userId: string,
    items: AdminCommitmentOverviewItem[],
    /** Prices Stripe couldn't be read for, so nothing on them was checked. */
    unreadable: {
        externalSubscriptionId: string,
        externalPriceId: string,
        error: string
    }[]
}

export type AdminCommitmentHealResult = {
    applied: {
        externalSubscriptionId: string,
        externalPriceId: string,
        code: string,
        detail: string
    }[],
    overview: AdminCommitmentOverview
}
